<script>
import UebungenLibraryIndexUebung from "@/uebungen-library/components/UebungenLibraryIndexUebung.vue";
import UebungenLibraryCuratedDashboard from "@/uebungen-library/components/UebungenLibraryCuratedDashboard.vue";
import {wait} from "@/helpers/functions_helpers.js";
import {debounce} from "lodash";
import {EventBus} from "@/hermeneus.eventbus/eventbus.js";
import {haxiosAPIClient} from "@/haxios/haxios.js";

export default {
  name: "uebungen-library-index",
  components: {
    UebungenLibraryIndexUebung,
    UebungenLibraryCuratedDashboard
  },
  data () {
    return {
      uebungen: [],
      filter_scope: "user",
      filter_category: "all",
      filter_search: "",
      isLoading: false,
      isAnimating: false,
      spreadPosition: {
        x: 0,
        y: 0
      }
    };
  },
  computed: {
    canCreateUebungen() {
      return ['admin', 'editor', 'tester'].includes(this.$userData?.role);
    },
    apiURLAppendix: function () {
      let scopeAppendix = `?scope=${this.filter_scope}`;
      let categoryAppendix = this.filter_category === "all" ? "" : `&category=${this.filter_category}`;
      let searchAppendix = this.filter_search === "" ? "" : `&search=${this.filter_search}`;
      return `${scopeAppendix}${categoryAppendix}${searchAppendix}`;
    },
  },
  methods: {
    async fetchUebungen () {
      this.isLoading = true;
      this.uebungen = [];
      await haxiosAPIClient.get(`/uebungen/index${this.apiURLAppendix}`).then(async (response) => {
        await wait(600);
        this.isLoading = false;


        this.uebungen = response.data.data.uebung;
      });

    },
    setScope (scope) {
      this.filter_scope = scope;
    },
    openAbsolveModal(uebung, position) {
      EventBus.$emit('uebungen-library__open-absolve-modal', {uebung, position});
    },
    closeAbsolveModal() {
      EventBus.$emit('close-absolve-uebung-modal');
    }
  },
  watch: {
    filter_scope: function () {
      this.fetchUebungen();
    },
    filter_category: function () {
      this.fetchUebungen();
    },
    filter_search: debounce(function () {
      this.fetchUebungen();
    }, 700),
  },
  mounted () {
    // Setze Default-Scope basierend auf Rolle
    if (!this.canCreateUebungen) {
      this.filter_scope = 'corpus';
    }

    this.fetchUebungen();

    // Globales Event für das Neuladen der Übungen
    EventBus.$on('requested-uebungen-fetch', () => {
      this.fetchUebungen();
    });
  },
  beforeDestroy() {
    // Event-Listener entfernen, um Memory-Leaks zu vermeiden
    EventBus.$off('requested-uebungen-fetch');
  }
}
</script>

<template>
  <div class="rounded shadow-md">
    <div class="uebungen-library__index__top-bar border border-t border-grey-300 flex flex-row justify-between xl-minus:flex-wrap rounded-t">
      <!-- Tab-Buttons (immer sichtbar) -->
      <div class="flex flex-col justify-between xl-minus:w-full" :class="{ 'xl:w-6/12': filter_scope !== 'corpus', 'w-full': filter_scope === 'corpus' }">

        <div class="flex flex-row justify-start" :class="{ 'w-2/3': filter_scope !== 'corpus' }">
          <button v-if="canCreateUebungen" @click="setScope('user')" :class="{'is-active-category text-primary-500': filter_scope === 'user', 'text-grey-600':filter_scope !== 'user' }"
                  class="btn-category flex flex-row items-center justify-center bg-white hover:text-primary-600 py-5 px-6 text-xl whitespace-nowrap">
            <i class="bx bx-user mr-2 text-2xl"></i>Meine Übungen
          </button>
          <button @click="setScope('corpus')" :class="{'is-active-category text-primary-500': filter_scope === 'corpus', 'text-grey-600':filter_scope !== 'corpus' }"
                  class="btn-category flex flex-row items-center justify-center bg-white hover:text-primary-600 py-5 px-6 text-xl whitespace-nowrap">
            <i class="hi hi-sentence mr-2 text-2xl"></i>Kuratierte Übungen
          </button>
          <button v-if="canCreateUebungen" @click="setScope('all')" :class="{'is-active-category text-primary-500': filter_scope === 'all', 'text-grey-600':filter_scope !== 'all' }"
                  class="btn-category flex flex-row items-center justify-center bg-white hover:text-primary-600 py-5 px-6 text-xl whitespace-nowrap">
            <i class="bx bx-list-ul mr-2 text-2xl"></i>Stöbern
          </button>
        </div>

        <!-- Suchfeld (nur bei 'user' und 'all') -->
        <div v-if="filter_scope !== 'corpus'" class="px-6 pt-5 pb-9 bg-white flex flex-row items-center w-8/12">
          <InputGroup>
            <InputGroupAddon>
              <div><i class="fas fa-search text-lg"></i></div>
            </InputGroupAddon>
            <InputText size="large" v-model="filter_search" placeholder="Suche"/>
            <InputGroupAddon>
              <button class="hover:text-primary-600" @click="this.filter_search=''"><i class="fas fa-times text-lg"></i></button>
            </InputGroupAddon>
          </InputGroup>
        </div>
      </div>

      <!-- Kategorie-Filter (nur bei 'user' und 'all') -->
      <template v-if="filter_scope !== 'corpus'">
        <Divider layout="vertical" class="mx-4 xl-minus:hidden"></Divider>
        <div class="uebungen-library_index__categories xl-minus:w-full xl:w-6/12 py-4 px-5 flex flex-row justify-between items-start flex-wrap minus:w-full select-none">
          <div class="flex flex-row items-center mr-4 py-2 px-3 rounded-md cursor-pointer hover:text-grey-700 h-14 transition-all"
               :class="{'text-primary-600 bg-grey-200': filter_category === 'all', 'text-grey-600': filter_category !== 'all'}">
            <RadioButton v-model="filter_category" :inputId="'all'" name="dynamic" :value="'all'" :size="'large'" variant="primary"/>
            <div class="flex flex-row items-center ml-2 cursor-pointer">
              <label :for="'all'" class="text-xl cursor-pointer"><i class="fa-solid fa-bars-staggered text-2xl mx-2"></i> Alle</label>
            </div>
          </div>
          <div v-for="(category, key) in this.$UebungenRegistry.registered_categories" :category_key="key"
               class="flex flex-row items-center mr-4 py-2 px-3 rounded-md cursor-pointer hover:text-grey-700 h-14 transition-all"
               :class="{'text-primary-600 bg-grey-200': filter_category === key, 'text-grey-600': filter_category !== 'key'}">
            <RadioButton v-model="filter_category" :inputId="key" name="dynamic" :value="key" :size="'large'" variant="primary"/>
            <div>
              <label :for="key" class="text-xl mt-1 flex flex-row items-center ml-2 whitespace-nowrap cursor-pointer">
                <hermeneus-icon height="32" width="32" :name="`hi-uebung-category-${key}`" color="current" class="mx-2"></hermeneus-icon>
                {{ category.title }}
              </label>
            </div>
          </div>
        </div>
      </template>
    </div>

    <!--INDEX-->
    <div class="transition-all">
      <!-- Kuratierte Übungen Dashboard -->
      <UebungenLibraryCuratedDashboard
        v-if="filter_scope === 'corpus'"
        :uebungen="uebungen"
        :is-loading="isLoading"
      />

      <!-- Standard-Listenansicht -->
      <template v-else>
        <div v-if="isLoading" class="flex flex-row justify-between flex-wrap p-5">
          <Skeleton height="6rem" borderRadius="12px" v-for="n in 60" :key="n" class="m-3"></Skeleton>
        </div>

        <div v-else-if="!isLoading && uebungen.length === 0">
          <div class="flex flex-col items-center justify-center py-10">
            <div class="text-2xl text-grey-600">Keine Übungen gefunden!</div>
          </div>
        </div>

        <div v-else class="flex flex-col p-5">
          <UebungenLibraryIndexUebung v-for="(uebung, index) in uebungen" :uebung="uebung" :uebung_key="Number(index)" @open-absolve-modal="openAbsolveModal"></UebungenLibraryIndexUebung>
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped>
.btn-category {
  transition: color ease-in-out 0.3s;
}

.btn-category.is-active-category {
  border-top: 4px solid #474e70;
  color: #474e70;
}
</style>